import React from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { BsShieldCheck, BsEye, BsHouseDoor, BsPeople, BsGraphUp, BsInfoCircle } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';
import './AIEthicsSection.css';

const AIEthicsSection: React.FC = () => {
  const navigate = useNavigate();

  const ethicsPrinciples = [
    {
      icon: <BsShieldCheck />,
      title: "Fair Housing Compliance",
      description: "Our AI strictly adheres to Fair Housing laws, ensuring equal opportunity for all home seekers"
    },
    {
      icon: <BsEye />,
      title: "Explainable AI",
      description: "Every recommendation comes with clear reasoning you can understand and verify"
    },
    {
      icon: <BsPeople />,
      title: "Zero Bias Search",
      description: "Our algorithms focus solely on your stated preferences, not demographic assumptions"
    },
    {
      icon: <BsGraphUp />,
      title: "Transparent Scoring",
      description: "See exactly how properties match your criteria with our open scoring system"
    }
  ];

  const commitments = [
    "No steering based on protected characteristics",
    "Equal access to all MLS listings",
    "Clear explanations for every match",
    "Regular bias audits by third parties"
  ];

  return (
    <section className="ai-ethics-section">
      <Container>
        <Row>
          <Col lg={12} className="text-center mb-5">
            <h2 className="section-title">
              <span className="house-emoji me-3">🏠</span>
              Our Commitment to Responsible AI
            </h2>
            <p className="section-subtitle">
              Building trust through transparency, fairness, and equal opportunity for all
            </p>
          </Col>
        </Row>

        <Row>
          <Col lg={6}>
            <div className="ethics-content">
              <h3 className="mb-4">AI That Puts Ethics First</h3>
              
              <div className="principles-grid">
                {ethicsPrinciples.map((principle, idx) => (
                  <div key={idx} className="principle-item">
                    <div className="principle-icon">{principle.icon}</div>
                    <div className="principle-text">
                      <h5>{principle.title}</h5>
                      <p>{principle.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="ethics-cta">
                <Button 
                  variant="primary" 
                  size="lg"
                  onClick={() => navigate('/ai-ethics')}
                >
                  <BsInfoCircle className="me-2" />
                  Learn About Our AI Ethics
                </Button>
              </div>
            </div>
          </Col>
          
          <Col lg={6}>
            <Card className="fair-housing-card">
              <Card.Body>
                <div className="fair-housing-header">
                  <div className="eho-logo-container">
                    <BsHouseDoor className="eho-icon" />
                    <span className="eho-text">EQUAL HOUSING<br />OPPORTUNITY</span>
                  </div>
                  <h4>Fair Housing Promise</h4>
                </div>
                
                <p className="fair-housing-text">
                  We believe everyone deserves equal access to housing opportunities. 
                  Our AI technology is designed to eliminate bias, not perpetuate it.
                </p>

                <div className="commitments-list">
                  <h5>Our Commitments:</h5>
                  <ul>
                    {commitments.map((commitment, idx) => (
                      <li key={idx}>
                        <BsShieldCheck className="text-success me-2" />
                        {commitment}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="transparency-badge">
                  <h5>Transparency in Action</h5>
                  <p>
                    Every search result includes an explanation of why properties 
                    were recommended, based solely on your stated preferences and 
                    objective property characteristics.
                  </p>
                  <div className="sample-explanation">
                    <strong>Example:</strong> "This property matches because: 
                    3 bedrooms (your requirement), within budget ($450K &lt; $500K), 
                    excellent schools (9/10 rating), 0.5 miles from parks."
                  </div>
                </div>

                <div className="audit-info">
                  <small className="text-muted">
                    <BsShieldCheck className="me-1" />
                    Last bias audit: September 2025 | Next audit: December 2025
                  </small>
                  <div className="mt-2">
                    <small className="text-muted">
                      <BsInfoCircle className="me-1" />
                      We audit search requests and chat communications to ensure compliance and improve our services
                    </small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* REMOVED: "Your Community, Your Intelligence" section with Chat Premium $10/mo tier
            Decision: 2026-04-12 — AI community intelligence should be free, not a paid tier.
            All AI features are included with the platform. No separate consumer subscription. */}

        <Row className="mt-4">
          <Col lg={12}>
            <div className="ethics-footer text-center">
              <p className="mb-0">
                <strong>Questions about our AI ethics?</strong> Contact our Fair Housing Officer at{' '}
                <a href="mailto:fairhousing@chat.realestate">fairhousing@chat.realestate</a>
              </p>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default AIEthicsSection;